import React, { useState, useEffect, useRef, MutableRefObject } from 'react';
import { IReactionDisposer, reaction } from 'mobx';
import { Off, PanelType } from '../interface';
import { pasteSpecialEventBus } from '@src/slide/application/service/copy-paste/core/paste/paste-special/event-bus';
import { CopyElementType, ICopyInfo } from '@src/slide/domain/ui-model/element-modeles/property/copy/copy-model/paste-special.interface';
import PasteSpecialPanel from '@src/framework/frame-pc/components/paste-special-panel';
import { IPasteSpecialServiceDelegates } from '@src/slide/application/service/copy-paste/core/paste/paste-special/paste-special-strategy/interface';
import { IEditorInstanceService } from '@src/slide/application/service/editor-instance-service/editor-instance-service.interface';
import { ITextBodyModel } from '@src/slide/application/service/copy-paste/core/store-manager';
import { SlideDataServices } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import type EditingStateManager from '@src/slide/application/service/editing-state-manager';

const defaultLeftOffset = 5;
const defaultTopOffset = 5;

interface ITextPasteSpecialPanelServices extends IPasteSpecialServiceDelegates {
    editorInstanceService: IEditorInstanceService;
    editingStateManager: EditingStateManager;
}

interface ITextPasteSpecialPanelProps {
    services: ITextPasteSpecialPanelServices;
    scale: number;
}

/**
 * 文字选择性粘贴选项
 * @param props
 * @returns
 */
const TextPasteSpecialPanel = (props: ITextPasteSpecialPanelProps) => {
    const [isShow, setShow] = useState(false);
    const [position, setPosition] = useState({ top: 0, left: 0 });
    const copyInfoRef = useRef({}) as MutableRefObject<ICopyInfo>;

    const getPosition = () => {
        const textEditor = props.services.editorInstanceService.getEditorInstance();
        if (!textEditor) {
            return null;
        }
        const expandedRect = textEditor.getExpandedRect();
        if (!expandedRect) {
            return null;
        }
        return {
            left: expandedRect.left + defaultLeftOffset,
            top: expandedRect.bottom + defaultTopOffset,
        };
    };
    // 组件挂载后，注册事件
    useEffect(() => {
        const disposePasteEvent = pasteSpecialEventBus.on(PanelType.TEXT_PASTE_PANEL, (textData: ITextBodyModel) => {
            copyInfoRef.current = {
                clipboardData: textData,
                type: CopyElementType.TEXT,
            };
            setShow(true);
        });

        return () => {
            // 组件销毁时取消监听
            disposePasteEvent();
        };
    }, []);

    // scale变化时，需重新计算位置
    useEffect(() => {
        if (!isShow) return;
        const position = getPosition();
        if (!position) {
            setShow(false);
            return;
        }
        setPosition(position);
    }, [props.scale, isShow]);

    let currentModelDisposer: IReactionDisposer;
    let textChangeDisposer: IReactionDisposer;
    let undoRedoOff: Off | undefined;
    let pasteCallbackOff: Off | undefined;
    // 添加当前元素选中状态的监听，在取消选中后，判断是否需要关闭选择性粘贴按钮
    const addElementSelectReaction = () => {
        currentModelDisposer?.();
        const currentModel = props.services.selectionService.selectedElements.getCurrentModel();
        currentModelDisposer = reaction(
            () => currentModel.selected,
            (selected) => {
                if (!selected) {
                    // 取消选中后，直接清除监听
                    currentModelDisposer?.();
                }
                // 如果是选择性粘贴导致的取消选中，不需要关闭按钮
                if (props.services.textSelectivePasteState?.checkIsPasting()) {
                    return;
                }
                if (!selected) {
                    setShow(false);
                }
            },
        );
    };
    const handleExitEditing = () => {
        // 如果是选择性粘贴导致的退出文本编辑态，不需要关闭按钮
        if (props.services.textSelectivePasteState?.checkIsPasting()) {
            return;
        }
        setShow(false);
    };
    useEffect(() => {
        // 显示的时候，添加一些监听逻辑
        if (isShow) {
            // 添加当前元素选中状态的监听
            addElementSelectReaction();

            // 退出文本编辑态时，判断是否需要关闭选择性粘贴按钮
            window.addEventListener('exitEditing', handleExitEditing);

            // 数据有变更时，判断是否需要关闭选择性粘贴按钮
            const sdkCommandEvents = SlideDataServices.SDKDataServices.EventManager()?.commandEvents;
            undoRedoOff = sdkCommandEvents?.onUndoRedoChange(() => {
                // 如果是选择性粘贴触发的 changeset 变更，不需要关闭按钮，同时需要更新按钮位置
                if (props.services.textSelectivePasteState?.checkIsPasting()) {
                    return;
                }
                // 其他数据变更时，关闭选择性粘贴按钮
                setShow(false);
            });

            // 文字有其他输入变更，关闭选择性粘贴按钮
            textChangeDisposer = reaction(
                () => props.services.editingStateManager.hasUnsavedTextContent,
                () => {
                    setShow(false);
                },
            );

            // 选择性粘贴完成后执行回调
            pasteCallbackOff = props.services.textSelectivePasteState?.executeAfterPaste(() => {
                requestAnimationFrame(() => {
                    // 更新按钮位置
                    const position = getPosition();
                    position && setPosition(position);
                    // 选中的元素可能已经变化，重新添加元素选中状态的监听
                    addElementSelectReaction();
                });
            });
        } else {
            currentModelDisposer?.();
            undoRedoOff?.();
            textChangeDisposer?.();
            pasteCallbackOff?.();
            window.removeEventListener('exitEditing', handleExitEditing);
        }
        return () => {
            currentModelDisposer?.();
            undoRedoOff?.();
            textChangeDisposer?.();
            pasteCallbackOff?.();
            window.removeEventListener('exitEditing', handleExitEditing);
        };
    }, [isShow]);

    return isShow ? (
        <PasteSpecialPanel
            position={position}
            copyInfo={copyInfoRef.current}
            serviceDelegates={props.services}
        />
    ) : null;
};

export default React.memo(TextPasteSpecialPanel);
